import type { ManageCompanySopSection } from "../types";

export const MANAGE_COMPANY_SOP_SECTIONS: ManageCompanySopSection[] = [
  {
    id: "before-you-start",
    titleKey: "manage-company-sop-before-start-title",
    titleFallback: "Before you start",
    steps: [
      {
        bodyKey: "manage-company-sop-before-start-1",
        bodyFallback:
          "This guide is for company admins only. Store staff and other roles will not see all of the options shown here."
      },
      {
        bodyKey: "manage-company-sop-before-start-2",
        bodyFallback:
          "Sign in with your company admin account. You need this role to manage employees, company details, and subscriptions."
      },
      {
        bodyKey: "manage-company-sop-before-start-3",
        bodyFallback:
          "From the Company page you can manage your team, update business details, and open your subscription portal."
      }
    ]
  },
  {
    id: "open-company-page",
    titleKey: "manage-company-sop-open-page-title",
    titleFallback: "Open the Company page",
    steps: [
      {
        bodyKey: "manage-company-sop-open-page-1",
        bodyFallback: "After you sign in, open Company from the left menu."
      },
      {
        bodyKey: "manage-company-sop-open-page-2",
        bodyFallback:
          "You will see three tabs at the top: Details, Employees, and Subscriptions. Each tab covers a different admin task."
      }
    ]
  },
  {
    id: "manage-employees",
    titleKey: "manage-company-sop-employees-title",
    titleFallback: "Manage employees",
    steps: [
      {
        bodyKey: "manage-company-sop-employees-1",
        bodyFallback:
          "Tap the Employees tab to see everyone who works for your company."
      },
      {
        bodyKey: "manage-company-sop-employees-2",
        bodyFallback:
          "The list shows each person's name, email, phone, and role. Use the search box or date filter to find someone quickly.",
        imageId: "employees-list"
      },
      {
        bodyKey: "manage-company-sop-employees-3",
        bodyFallback:
          "Each row has an Actions column. Use the view icon to open one employee's full record."
      }
    ]
  },
  {
    id: "add-employee",
    titleKey: "manage-company-sop-add-employee-title",
    titleFallback: "Add a new employee",
    steps: [
      {
        bodyKey: "manage-company-sop-add-employee-1",
        bodyFallback:
          "On the Employees tab, click the purple Add New Employee button."
      },
      {
        bodyKey: "manage-company-sop-add-employee-2",
        bodyFallback:
          "A form will open. You can add a photo by clicking the camera icon, but this is optional.",
        imageId: "add-employee"
      },
      {
        bodyKey: "manage-company-sop-add-employee-3",
        bodyFallback:
          "Enter the employee's first name, last name, email, and phone number."
      },
      {
        bodyKey: "manage-company-sop-add-employee-4",
        bodyFallback:
          "Choose the Store where this person will work, then choose their Role. The role controls what they can see and do in the system."
      },
      {
        bodyKey: "manage-company-sop-add-employee-5",
        bodyFallback:
          "When everything looks correct, click Add Employee. The new person will appear in your employee list."
      }
    ]
  },
  {
    id: "update-employee",
    titleKey: "manage-company-sop-update-employee-title",
    titleFallback: "Update an existing employee",
    steps: [
      {
        bodyKey: "manage-company-sop-update-employee-1",
        bodyFallback:
          "On the Employees tab, find the person you want to change and click the view icon in the Actions column."
      },
      {
        bodyKey: "manage-company-sop-update-employee-2",
        bodyFallback: "On the employee detail page, click Edit."
      },
      {
        bodyKey: "manage-company-sop-update-employee-3",
        bodyFallback:
          "Update the fields you need, such as name, email, or phone number. You can also change the profile photo.",
        imageId: "update-employee"
      },
      {
        bodyKey: "manage-company-sop-update-employee-4",
        bodyFallback:
          "Click Update Employee to save your changes, or Cancel to close the form without saving."
      }
    ]
  },
  {
    id: "company-details",
    titleKey: "manage-company-sop-details-title",
    titleFallback: "View and update company information",
    steps: [
      {
        bodyKey: "manage-company-sop-details-1",
        bodyFallback:
          "Tap the Details tab to see your company's main business information."
      },
      {
        bodyKey: "manage-company-sop-details-2",
        bodyFallback:
          "Review the company name, business type, currency, tax settings, registration number, and address.",
        imageId: "company-details"
      },
      {
        bodyKey: "manage-company-sop-details-3",
        bodyFallback: "To make changes, click the purple Edit Company button."
      },
      {
        bodyKey: "manage-company-sop-details-4",
        bodyFallback:
          "Update the fields you need in the form. This can include the company name, currency, VAT settings, registration number, and address.",
        imageId: "edit-company"
      },
      {
        bodyKey: "manage-company-sop-details-5",
        bodyFallback:
          "Save your changes when you are done. Your team will see the updated details across the system."
      }
    ]
  },
  {
    id: "subscriptions",
    titleKey: "manage-company-sop-subscriptions-title",
    titleFallback: "Manage subscriptions",
    steps: [
      {
        bodyKey: "manage-company-sop-subscriptions-1",
        bodyFallback:
          "Tap the Subscriptions tab to manage your service plan and billing."
      },
      {
        bodyKey: "manage-company-sop-subscriptions-2",
        bodyFallback:
          "Click View subscriptions to open the subscriptions portal.",
        imageId: "subscriptions-tab"
      },
      {
        bodyKey: "manage-company-sop-subscriptions-3",
        bodyFallback:
          "On the My Subscription page you can see your current plan, start and end dates, next billing date, and which features are included.",
        imageId: "my-subscription"
      },
      {
        bodyKey: "manage-company-sop-subscriptions-4",
        bodyFallback:
          "Use the Plans and Subscriptions tabs at the top to compare plans or review past and current subscriptions."
      },
      {
        bodyKey: "manage-company-sop-subscriptions-5",
        bodyFallback:
          "If you need help with billing or plan changes, contact support using the help buttons on the page."
      }
    ]
  }
];
